import { Link, useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ArrowRight, Check, MapPin, Quote, Tag } from "lucide-react";
import { BrandStrip, type BrandKey } from "@/components/BrandStrip";
import { ContextualLinks } from "@/components/ContextualLinks";
import { longformFor } from "@/data/serviceLongform";

import {
  cityLinksForTopic,
  guidesForTopics,
  servicesForTopics,
  type TopicKey,
} from "@/lib/internalLinks";

type Faq = { q: string; a: string };
type Testimonial = { quote: string; name: string; location: string };
type AreaLink = { slug: string; name: string };
type RelatedService = { to: string; label: string; desc?: string };

type Props = {
  eyebrow: string;
  title: string;
  intro: ReactNode;
  image?: string;
  imageSrcSet?: string;
  imageSizes?: string;
  imageAlt?: string;
  video?: string;
  videoType?: string;
  features: { title: string; desc: string }[];
  bullets: string[];
  bulletsHeading?: string;
  ctaHeading?: string;
  startingPrice?: string;
  priceNote?: string;
  faqs?: Faq[];
  testimonials?: Testimonial[];
  serviceAreas?: AreaLink[];
  relatedServices?: RelatedService[];
  brands?: BrandKey[];
  brandsTitle?: string;
  /** Topic keys driving contextual internal links to guides and suburb pages. */
  topics?: TopicKey[];
};

export function ServiceDetail({ eyebrow, title, intro, image, imageSrcSet, imageSizes, imageAlt, video, videoType = "video/mp4", features, bullets, bulletsHeading, ctaHeading, startingPrice, priceNote, faqs, testimonials, serviceAreas, relatedServices, brands, brandsTitle, topics }: Props) {
  const { pathname } = useLocation();
  const longform = longformFor(pathname);
  // Grammatical mid-sentence service name: drop trailing "near me" / city suffixes, then lowercase.
  const servicePhrase = eyebrow
    .replace(/\s+near me$/i, "")
    .replace(/\s+(denver|lakewood|aurora|boulder|parker|littleton|highlands ranch|castle rock|castle pines|cherry creek)$/i, "")
    .toLowerCase();
  return (


    <>
      <section className="architectural-grid relative min-h-[68svh] overflow-hidden border-b border-border">
        {(video || image) && (
          <div className="absolute inset-0">
            {video ? (
              <video
                className="h-full w-full object-cover opacity-50"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                poster={image}
                aria-hidden="true"
                onEnded={(event) => {
                  event.currentTarget.currentTime = 0;
                  void event.currentTarget.play();
                }}
              >
                <source src={video} type={videoType} />
              </video>
            ) : (
              <img src={image} srcSet={imageSrcSet} sizes={imageSizes} alt={imageAlt ?? ""} className="h-full w-full object-cover opacity-50" width={1280} height={896} loading="eager" fetchPriority="high" decoding="async" />
            )}
            <div className="absolute inset-0 bg-gradient-hero" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/20" />
          </div>
        )}
        <div className="relative container mx-auto flex min-h-[68svh] items-center px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="eyebrow flex items-center gap-4 text-brand-text"><span className="h-px w-10 bg-brand/70" />{eyebrow}</p>
            <h1 className="mt-7 font-display text-5xl font-medium leading-[1.02] md:text-7xl">{title}</h1>
            <p className="mt-7 max-w-2xl border-l border-brand/50 pl-6 text-lg text-muted-foreground md:text-xl">{intro}</p>
          </div>
        </div>
      </section>

      <section aria-labelledby="quick-answer" className="container mx-auto px-6 pt-14">
        <div className="max-w-3xl border-l border-brand/50 pl-6">
          <h2 id="quick-answer" className="eyebrow">Quick answer</h2>
          <p className="mt-3 text-lg">
            Apex AV designs, installs, and supports {servicePhrase} for homes and businesses across Denver and the Front Range, from Boulder to Castle Rock.
            {startingPrice ? ` Projects typically start around ${startingPrice}${priceNote ? ` (${priceNote})` : ""}.` : ""}
            {" "}Every project begins with a free in-home consultation. Call (303) 317-3233 to schedule.
          </p>
          {faqs && faqs[0] && (
            <p className="mt-4 text-muted-foreground"><strong className="text-foreground">{faqs[0].q}</strong> {faqs[0].a}</p>
          )}
        </div>
      </section>

      <section className="section-rail container mx-auto px-6 py-20 md:py-28">
        <div className={image ? "grid gap-10 md:grid-cols-2 items-start" : "max-w-3xl mx-auto"}>
          {image && (
            <div className="overflow-hidden border border-border shadow-card">
              <img src={image} alt={imageAlt ?? ""} loading="lazy" width={1280} height={896} className="h-full w-full object-cover" />
            </div>
          )}
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold">{bulletsHeading ?? `What's included with ${servicePhrase}`}</h2>
            <ul className="mt-6 space-y-3">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-brand-text mt-0.5 shrink-0" />
                  <span className="text-muted-foreground">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-14">
        <div className="grid gap-6 md:grid-cols-3">
          {features.map((f) => (
            <div key={f.title} className="border-t border-brand/50 bg-card/40 p-6 shadow-card">
              <h3 className="text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {longform.length > 0 && (
        <section className="container mx-auto px-6 py-12">
          <div className="max-w-3xl space-y-12">
            {longform.map((s) => (
              <article key={s.heading}>
                <h2 className="font-display text-2xl md:text-3xl font-bold">{s.heading}</h2>
                <div className="mt-4 space-y-4">
                  {s.paragraphs.map((p) => (
                    <p key={p.slice(0, 40)} className="text-muted-foreground leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}



      {brands && brands.length > 0 && (
        <section className="container mx-auto px-6 py-12">
          <BrandStrip
            variant="inline"
            brands={brands}
            eyebrow="Trusted brands"
            title={brandsTitle ?? `${eyebrow} — brands we install.`}
          />
        </section>
      )}



      {startingPrice && (
        <section className="container mx-auto px-6 py-12">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-10 shadow-card flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-lg bg-gradient-accent flex items-center justify-center shrink-0">
                <Tag className="h-5 w-5 text-accent-foreground" />
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wider text-brand-text">Transparent pricing</p>
                <p className="mt-1 font-display text-2xl md:text-3xl font-bold">Starting at {startingPrice}</p>
                {priceNote && <p className="mt-2 text-sm text-muted-foreground max-w-xl">{priceNote}</p>}
              </div>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-3 text-sm font-semibold hover:border-brand/50 transition shrink-0">
              Get an exact quote <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {testimonials && testimonials.length > 0 && (
        <section className="container mx-auto px-6 py-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold">What Denver homeowners say</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-xl border border-border bg-card p-6 shadow-card">
                <Quote className="h-6 w-6 text-brand-text opacity-60" />
                <blockquote className="mt-4 text-muted-foreground">"{t.quote}"</blockquote>
                <figcaption className="mt-5 text-sm">
                  <span className="font-semibold">{t.name}</span>
                  <span className="text-muted-foreground"> — {t.location}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {faqs && faqs.length > 0 && (
        <section className="container mx-auto px-6 py-16">
          <div className="max-w-3xl">
            <h2 className="font-display text-3xl md:text-4xl font-bold">Frequently asked questions</h2>
            <dl className="mt-8 space-y-6">
              {faqs.map((f) => (
                <div key={f.q} className="rounded-xl border border-border bg-card p-6 shadow-card">
                  <dt className="font-semibold">{f.q}</dt>
                  <dd className="mt-2 text-muted-foreground">{f.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {serviceAreas && serviceAreas.length > 0 && (
        <section className="container mx-auto px-6 py-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold">{eyebrow} across the Denver Metro</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">We install across every Front Range community. Pick your neighborhood for local details.</p>
          <ul className="mt-8 flex flex-wrap gap-3">
            {serviceAreas.map((a) => (
              <li key={a.slug}>
                <Link
                  to="/service-areas/$slug"
                  params={{ slug: a.slug }}
                  className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm hover:border-brand/50 transition"
                >
                  <MapPin className="h-3.5 w-3.5 text-brand-text" />
                  {a.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {relatedServices && relatedServices.length > 0 && (
        <section className="container mx-auto px-6 py-16 border-t border-border">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Related services</h2>
          <p className="mt-3 text-muted-foreground max-w-2xl">Most {eyebrow.toLowerCase()} projects pair naturally with these.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {relatedServices.map((r) => (
              <Link
                key={r.to}
                to={r.to as never}
                className="group rounded-xl border border-border bg-card p-5 shadow-card hover:border-brand/50 transition"
              >
                <h3 className="font-display text-lg font-semibold group-hover:text-brand-text">{r.label}</h3>
                {r.desc && <p className="mt-2 text-sm text-muted-foreground">{r.desc}</p>}
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand-text">
                  Explore {r.label} <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {topics && topics.length > 0 && (
        <ContextualLinks
          heading={`${eyebrow} guides and local service areas`}
          intro="Background reading before you spec the system, plus the Denver suburbs where we install it most."
          groups={[
            {
              heading: "Guides worth reading first",
              icon: "guide",
              links: guidesForTopics(topics, 4),
            },
            {
              heading: "Works well with",
              icon: "service",
              links: servicesForTopics(topics, 3, pathname),
            },

            {
              heading: "Where we install this",
              icon: "area",
              links: cityLinksForTopic(topics[0], 5),
            },
          ]}
        />
      )}

      <section className="container mx-auto px-6 py-20">
          <div className="architectural-grid border-y border-border bg-card/50 p-10 text-center md:p-14">
          <h2 className="font-display text-3xl md:text-4xl font-bold">{ctaHeading ?? `Get a free ${eyebrow.toLowerCase()} consultation in Denver.`}</h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">Free in-home consultations across the Denver Metro Area.</p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow">
            Book a Free Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
